
import { createSlice } from '@reduxjs/toolkit'

const initialState = {
    cartItems: [],
    directPurchaseItem: null,
    totalAmount: 0,
    totalQuantity: 0,
    lastActionStatus: null
}

const cartSlice = createSlice({
    name: 'cart',
    initialState,
    reducers: {
       addItem: (state, action) => {
            const newItem = action.payload;
            const existingItem = state.cartItems.find(
                item => item.itemId === newItem.itemId
            );

            const maxQty = newItem.stockQty ?? Infinity;
            const qtyToAdd = newItem.quantity || 1;

            // If no stock at all, block
            if (!maxQty || maxQty <= 0) {
                state.lastActionStatus = { 
                    success: false, 
                    message: "Exceeds available stock",
                    product: newItem  
                };
                return; // Out of stock
            }

            if (!existingItem) {
                // 🚨 If initial request exceeds stock, block
                if (qtyToAdd > maxQty) {
                    state.lastActionStatus = { 
                        success: false, 
                        message: "Exceeds available stock",
                        product: newItem 
                    };
                    return; // Exceeds stock, don't add
                }

                state.cartItems.push({
                    itemId: newItem.itemId,
                    id: newItem.id,
                    productName: newItem.variationName || newItem.productName,
                    variationName: newItem.variationName || '',
                    imgUrl: newItem.imgUrl,
                    price: newItem.price,
                    weight: newItem?.weight,
                    productSize: newItem?.productSize,
                    size: newItem?.size,
                    color: newItem?.color || null,
                    quantity: qtyToAdd,
                    totalPrice: newItem.price * qtyToAdd
                });
            } else {
                // 🚨 Check total against stock
                if (existingItem.quantity + qtyToAdd > maxQty) {
                    state.lastActionStatus = { success: false, message: "Exceeds available stock" };
                    return; // Block adding — would exceed stock
                }

                existingItem.quantity += qtyToAdd;
                existingItem.totalPrice = existingItem.quantity * existingItem.price;
            }

            // Recalculate totals
            state.totalQuantity = state.cartItems.reduce(
                (total, item) => total + item.quantity,
                0
            );
            state.totalAmount = state.cartItems.reduce(
                (total, item) => total + item.totalPrice,
                0
            );

            state.lastActionStatus = { 
                success: true, 
                message: "Product added to your bag", 
                product: newItem 
            };
        },

        // reset status when needed (optional)
        clearActionStatus: (state) => {
            state.lastActionStatus = null;
        },


        setDirectPurchaseItem: (state, action) => {
            state.directPurchaseItem = action.payload;
        },

        clearDirectPurchaseItem: (state) => {
            state.directPurchaseItem = null;
        },

        clearCart: (state) => {
            state.cartItems = [];
            state.totalAmount = 0;
            state.totalQuantity = 0;
        },

        deleteItem: (state, action) => {
            const {id, size} = action.payload
            const existingItemIndex = state.cartItems.findIndex((item) => (item.id === id && item.size === size));

            if (existingItemIndex !== -1) {
                const existingItem = state.cartItems[existingItemIndex];
                state.totalQuantity -= existingItem.quantity;
                state.totalAmount -= existingItem.totalPrice;

                state.cartItems.splice(existingItemIndex, 1);
            }
        },

        updateItemQuantity: (state, action) => {
            const { id, size, quantity } = action.payload;
            
            // Find item by id and size
            const existingItem = state.cartItems.find(item => item.id === id && item.size === size);

            if (existingItem) {
                existingItem.quantity = quantity;
                existingItem.totalPrice = existingItem.quantity * existingItem.price;
            }

            // ✅ Always recalculate total quantity
            state.totalQuantity = state.cartItems.reduce((total, item) => total + item.quantity, 0);

            // ✅ Always recalculate total amount
            state.totalAmount = state.cartItems.reduce((total, item) => total + item.totalPrice, 0);
        }
    }
});

export const cartActions = cartSlice.actions

export default cartSlice.reducer